import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, Settings as SettingsIcon, Archive, LogOut, ShieldCheck, CreditCard, FileText, Wallet, LifeBuoy, Phone, X, Banknote, KeyRound, Clock, Landmark, Calculator, ChevronDown, HandCoins, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import RequestCallDialog from "@/components/RequestCallDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import BillingBanner from "@/components/BillingBanner";
import TrialCountdownBanner from "@/components/TrialCountdownBanner";
import api from "@/lib/api";
import { resetPersistedScopes } from "@/hooks/usePersistedState";

// Struttura del menu — ordine richiesto dall'utente:
// Dashboard → Foglio cassa agenzia → gruppo "Contabilità" (Sospesi, Controllo C/C,
// Storico versamenti, Storico fogli cassa, Flusso di cassa) → Utenze → Impostazioni
// → Abbonamento → Aiuto/Assistenza.
const NAV_STRUCTURE = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/admin/foglio-cassa-agenzia", label: "Foglio cassa agenzia", icon: FileText, testId: "nav-foglio-agenzia" },
  {
    type: "group",
    key: "contabilita",
    label: "Contabilità",
    icon: Calculator,
    testId: "nav-group-contabilita",
    children: [
      { to: "/admin/sospesi", label: "Sospesi", icon: Clock, testId: "nav-sospesi", sospesiOnly: true },
      { to: "/admin/controllo-cc", label: "Controllo C/C", icon: Landmark, testId: "nav-controllo-cc", controlloCCOnly: true },
      { to: "/admin/da-versare", label: "Da versare", icon: HandCoins, testId: "nav-da-versare" },
      { to: "/admin/storico-versamenti", label: "Storico versamenti", icon: Banknote, testId: "nav-storico-versamenti" },
      { to: "/admin/storico", label: "Storico fogli cassa", icon: Archive, testId: "nav-storico" },
      { to: "/admin/flusso-cassa", label: "Flusso di cassa", icon: Wallet, testId: "nav-flusso-cassa" },
    ],
  },
  { to: "/admin/collaboratori", label: "Utenze", icon: Users, testId: "nav-collaboratori", hideOnSolo: true, hideForConsultazione: true },
  { to: "/admin/impostazioni", label: "Impostazioni", icon: SettingsIcon, testId: "nav-impostazioni", hideForConsultazione: true },
  { to: "/admin/abbonamento", label: "Abbonamento", icon: CreditCard, testId: "nav-abbonamento", hideForConsultazione: true },
  { to: "/admin/aiuto", label: "Aiuto / Assistenza", icon: LifeBuoy, testId: "nav-aiuto" },
];

export default function AdminLayout() {
  const { user, logout, branding } = useAuth();
  const nav = useNavigate();
  const displayName = branding.nome_agenzia || "Incassi Giornalieri";
  const isSoloPlan = user?.agency?.plan_id === "solo";
  const isConsultazione = user?.role === "consultazione";
  const sospesiEnabled = !!user?.agency?.sospesi_abilitati;
  const controlloCCEnabled = !!user?.agency?.controllo_cc_enabled;

  // Applica classe globale al body per attivare gli stili di sola lettura
  // (nasconde pulsanti di scrittura e disabilita campi editabili in tutte le
  // pagine admin). Il backend blocca comunque le mutazioni con 403.
  useEffect(() => {
    if (isConsultazione) {
      document.body.classList.add("role-consultazione");
    } else {
      document.body.classList.remove("role-consultazione");
    }
    return () => document.body.classList.remove("role-consultazione");
  }, [isConsultazione]);

  // Rotte precluse al ruolo consultazione: se atterra qui via URL diretto,
  // reindirizza alla dashboard. NON include foglio-cassa-agenzia (deve vederlo
  // in sola lettura).
  const location0 = useLocation();
  const blockedPathsForConsultazione = [
    "/admin/impostazioni",
    "/admin/abbonamento",
    "/admin/collaboratori",
  ];
  const isOnBlockedPath = isConsultazione && blockedPathsForConsultazione.some((p) => location0.pathname.startsWith(p));

  // Filtra child in base ai flag di feature/piano, poi rimuove i gruppi vuoti.
  const passFilters = (it) => {
    if (isSoloPlan && it.hideOnSolo) return false;
    if (isConsultazione && it.hideForConsultazione) return false;
    if (it.sospesiOnly && !sospesiEnabled) return false;
    if (it.controlloCCOnly && !controlloCCEnabled) return false;
    return true;
  };
  const navStructure = NAV_STRUCTURE
    .map((it) => {
      if (it.type === "group") {
        const children = (it.children || []).filter(passFilters);
        return children.length ? { ...it, children } : null;
      }
      return passFilters(it) ? it : null;
    })
    .filter(Boolean);

  // Onboarding call: mostra il banner se status === "pending" e abbonamento attivo
  const [callOpen, setCallOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);

  // Gruppo "Contabilità" — stato aperto/chiuso persistito in localStorage;
  // si apre automaticamente se l'utente sta visitando una delle sue voci figlie.
  const location = useLocation();
  // Filtri persistenti: restano finché non si cambia pagina del portale
  useEffect(() => { resetPersistedScopes(location.pathname); }, [location.pathname]);
  const groupsWithActiveChild = navStructure
    .filter((it) => it.type === "group" && (it.children || []).some((c) => location.pathname.startsWith(c.to)))
    .map((it) => it.key);
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("admin-nav-groups") || "{}");
      const initial = { ...stored };
      groupsWithActiveChild.forEach((k) => { initial[k] = true; });
      return initial;
    } catch { return {}; }
  });
  useEffect(() => {
    // Forza apertura del gruppo se navighi su un suo figlio
    if (groupsWithActiveChild.length === 0) return;
    setOpenGroups((g) => {
      const next = { ...g };
      let changed = false;
      groupsWithActiveChild.forEach((k) => {
        if (!next[k]) { next[k] = true; changed = true; }
      });
      return changed ? next : g;
    });
  }, [location.pathname]);
  const toggleGroup = (key) => {
    setOpenGroups((g) => {
      const next = { ...g, [key]: !g[key] };
      try { localStorage.setItem("admin-nav-groups", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const subActive = ["active", "trialing"].includes(branding.subscription_status || "");
  useEffect(() => {
    if (branding.onboarding_call_status === "pending" && subActive) {
      setShowOnboardingBanner(true);
    } else {
      setShowOnboardingBanner(false);
    }
  }, [branding.onboarding_call_status, subActive]);

  const markStatus = async (status) => {
    try {
      await api.post("/agency/onboarding-call/mark", { status });
      window.dispatchEvent(new Event("agency:updated"));
    } catch {
      /* non-blocking */
    }
  };

  const dismissOnboarding = async () => {
    setShowOnboardingBanner(false);
    await markStatus("dismissed");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {isOnBlockedPath && <Navigate to="/admin" replace />}
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-100 hidden md:flex md:flex-col" data-testid="admin-sidebar">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-10 h-10 rounded-lg object-contain bg-white p-1" data-testid="header-logo" />
            ) : (
              <ShieldCheck className="w-6 h-6 text-blue-400" />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-heading font-bold text-base truncate" data-testid="header-agency-name">{displayName}</div>
              {branding.header_subtitle && (
                <div className="text-xs text-slate-400 truncate">{branding.header_subtitle}</div>
              )}
            </div>
          </div>
          <div className="text-xs text-slate-400 mt-2">{isConsultazione ? "Area Consultazione" : (isSoloPlan ? "Area utente singolo" : "Area Agente")}</div>
          {isConsultazione && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-[10px] font-semibold uppercase tracking-wider" data-testid="badge-sola-lettura">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Sola lettura
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navStructure.map((item) => {
            if (item.type === "group") {
              const isOpen = !!openGroups[item.key];
              const hasActiveChild = (item.children || []).some((c) => location.pathname.startsWith(c.to));
              return (
                <div key={item.key} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.key)}
                    data-testid={item.testId}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      hasActiveChild
                        ? "bg-slate-800/60 text-white"
                        : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                    }`}
                    aria-expanded={isOpen}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="pl-3 border-l border-slate-800 ml-4 space-y-1" data-testid={`${item.testId}-children`}>
                      {item.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          end={child.end}
                          data-testid={child.testId}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              isActive
                                ? "bg-slate-800 text-white font-medium"
                                : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                            }`
                          }
                        >
                          <child.icon className="w-3.5 h-3.5" />
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-slate-800 text-white"
                      : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="text-xs text-slate-400 mb-2">Connesso come</div>
          <div className="font-heading font-semibold text-sm">{user?.nome} {user?.cognome}</div>
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <Button
            data-testid="change-password-btn"
            onClick={() => setChangePwdOpen(true)}
            variant="ghost"
            className="w-full mt-3 text-slate-300 hover:bg-slate-800 hover:text-white justify-start"
          >
            <KeyRound className="w-4 h-4 mr-2" /> Cambia password
          </Button>
          <Button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            variant="outline"
            className="w-full mt-2 bg-slate-800 border-slate-700 hover:bg-slate-700 text-slate-100 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 text-white px-4 py-3 flex items-center justify-between z-40">
        <div className="flex items-center gap-2 min-w-0">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="Logo" className="w-7 h-7 rounded object-contain bg-white p-0.5" />
          ) : (
            <ShieldCheck className="w-5 h-5 text-blue-400" />
          )}
          <span className="font-heading font-semibold text-sm truncate">{displayName}</span>
        </div>
        <Button
          data-testid="logout-btn-mobile"
          onClick={async () => { await logout(); nav("/login"); }}
          variant="ghost"
          size="sm"
          className="text-slate-200"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <main className="flex-1 min-w-0 md:ml-0 pt-14 md:pt-0">
        <BillingBanner />
        <TrialCountdownBanner />
        {isConsultazione && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2 text-sm text-amber-900" data-testid="banner-sola-lettura">
            <Eye className="w-4 h-4 shrink-0" />
            <span><b>Modalità sola consultazione</b> — puoi visualizzare foglio cassa, sospesi, versamenti e statistiche. Le operazioni di modifica sono disabilitate.</span>
          </div>
        )}
        {/* Mobile bottom nav — appiattisce il menu (i figli del gruppo Contabilità
            entrano nella lista mantenendo l'ordine); troppe voci sono comunque
            visibili tramite scroll orizzontale nativo della grid. */}
        {(() => {
          const flat = navStructure.flatMap((it) => it.type === "group" ? it.children : [it]);
          return (
            <nav
              className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 grid z-40 overflow-x-auto"
              style={{ gridTemplateColumns: `repeat(${flat.length}, minmax(72px, 1fr))` }}
            >
              {flat.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center py-2 text-[10px] ${
                      isActive ? "text-slate-900" : "text-slate-500"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5 mb-1" />
                  <span className="truncate max-w-full px-1">{item.label.split(" ")[0]}</span>
                </NavLink>
              ))}
            </nav>
          );
        })()}
        <div className="p-4 md:p-8 pb-24 md:pb-8">
          {showOnboardingBanner && (
            <div
              className="mb-5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-emerald-50 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
              data-testid="onboarding-call-banner"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-slate-900">
                  Benvenuto! Vuoi una call gratuita di configurazione?
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Ti aiutiamo a configurare collaboratori, email, metodi di pagamento e a personalizzare l&apos;app per la tua agenzia.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={dismissOnboarding}
                  data-testid="onboarding-dismiss"
                  className="bg-white"
                >
                  <X className="w-4 h-4 mr-1" /> Più tardi
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCallOpen(true)}
                  data-testid="onboarding-prenota"
                >
                  <Phone className="w-4 h-4 mr-2" /> Prenota la call
                </Button>
              </div>
            </div>
          )}
          <Outlet />
        </div>
      </main>
      <RequestCallDialog
        open={callOpen}
        onOpenChange={(v) => {
          setCallOpen(v);
          if (!v && branding.onboarding_call_status === "pending") {
            // L'utente ha chiuso senza inviare: lascia "pending" per riproporlo
          }
        }}
        source="onboarding"
        defaults={{
          nome: `${user?.nome || ""} ${user?.cognome || ""}`.trim(),
          email: user?.email || "",
          agenzia: branding.nome_agenzia || "",
        }}
        onSuccess={async () => {
          await markStatus("requested");
          setShowOnboardingBanner(false);
        }}
      />
      <ChangePasswordDialog open={changePwdOpen} onOpenChange={setChangePwdOpen} />
    </div>
  );
}
